import React from 'react';
import { Helmet } from 'react-helmet-async';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

/**
 * Composant SEO pour gérer les métadonnées de toutes les pages
 * 
 * @param {string} title - Titre de la page (sera combiné avec APP_NAME)
 * @param {string} description - Description meta
 * @param {string} image - URL de l'image Open Graph
 * @param {string} url - URL canonique de la page
 * @param {string} type - Type Open Graph (article, website, etc.)
 * @param {string} author - Auteur de la page
 * @param {string} keywords - Mots-clés SEO
 */
class SEO extends React.Component {
    render() {
        const {
            title,
            description,
            image,
            url,
            type = 'website',
            author,
            keywords,
            publishedTime,
            modifiedTime,
            jsonLd,
            _preferences
        } = this.props;

        const appName = window.APP_NAME || 'Portfolio';
        const siteAuthor = _preferences?._author || 'Ahmed Karim';
        const siteEmail = _preferences?._email || 'ahmed.karim64@outlook.fr';
        
        // Construire le titre complet
        const fullTitle = title ? `${title} | ${appName}` : appName;
        
        // Description par défaut
        const metaDescription = description || `Portfolio de ${siteAuthor} - Développeur et créateur de contenu`;
        
        // URL canonique (utiliser l'URL actuelle si non fournie)
        const canonicalUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
        
        // Image par défaut : la carte de partage générée par l'API (1200x630).
        // Le logo brut faisait une vignette carrée illisible sur LinkedIn.
        const ogImage = image || `${window.location.origin}/og/page.png`;
        
        // Mots-clés par défaut
        const metaKeywords = keywords || `portfolio, ${siteAuthor}, développement, web, articles, veille technologique`;

        return (
            <Helmet>
                {/* Titre */}
                <title>{fullTitle}</title>
                
                {/* Meta tags de base */}
                <meta name="description" content={metaDescription} />
                {metaKeywords && <meta name="keywords" content={metaKeywords} />}
                {author && <meta name="author" content={author} />}
                
                {/* Open Graph / Facebook */}
                <meta property="og:type" content={type} />
                <meta property="og:title" content={fullTitle} />
                <meta property="og:description" content={metaDescription} />
                <meta property="og:image" content={ogImage} />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />
                <meta property="og:image:alt" content={fullTitle} />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:site_name" content={appName} />
                <meta property="og:locale" content="fr_FR" />

                {/* Article : dates de publication (ignorées sur les autres types) */}
                {type === 'article' && publishedTime && (
                    <meta property="article:published_time" content={publishedTime} />
                )}
                {type === 'article' && modifiedTime && (
                    <meta property="article:modified_time" content={modifiedTime} />
                )}
                {type === 'article' && author && (
                    <meta property="article:author" content={author} />
                )}

                {/* Twitter Card */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={fullTitle} />
                <meta name="twitter:description" content={metaDescription} />
                <meta name="twitter:image" content={ogImage} />
                <meta name="twitter:image:alt" content={fullTitle} />

                {/* URL canonique */}
                <link rel="canonical" href={canonicalUrl} />

                {/* Données structurées. Google exécute le JS, il les voit ;
                    les robots d'aperçu social passent eux par /social-preview. */}
                {jsonLd && (
                    <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
                )}

                {/* Autres meta tags */}
                <meta name="theme-color" content="#0f1936" />
            </Helmet>
        );
    }
}

SEO.propTypes = {
    title: PropTypes.string,
    description: PropTypes.string,
    image: PropTypes.string,
    url: PropTypes.string,
    type: PropTypes.string,
    author: PropTypes.string,
    keywords: PropTypes.string,
    publishedTime: PropTypes.string,
    modifiedTime: PropTypes.string,
    jsonLd: PropTypes.object,
    _preferences: PropTypes.object
};

const mapStateToProps = state => ({
    _preferences: state._preferences
});

export default connect(mapStateToProps)(SEO);
